/**
 * Perfis de regra de preço (D89): conjuntos nomeados de margem, seguro, custo fixo e Simples ("Geral", "Competitivo"…),
 * aplicados na precificação da licitação. Valores definidos pelo admin; alterações ficam na auditoria e cada preço
 * gravado guarda a foto dos valores usados.
 */
import { useState } from "react";
import { useGetList, useNotify, usePermissions } from "react-admin";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddOutlined";
import { formatarPercentual } from "../lib/formato";
import type { Permissoes } from "../lib/provedores";
import { supabase } from "../lib/supabase";

interface Perfil {
  id: string;
  nome: string;
  margem: number;
  seguro: number;
  custo_fixo: number;
  simples: number;
  observacao: string | null;
  ativo: boolean;
}

const CAMPOS = [
  { id: "margem", nome: "Margem" },
  { id: "seguro", nome: "Seguro" },
  { id: "custo_fixo", nome: "Custo fixo" },
  { id: "simples", nome: "Simples" },
] as const;

type Campo = (typeof CAMPOS)[number]["id"];

const pctTexto = (f: number | null | undefined) => (f === null || f === undefined ? "" : String(Math.round(Number(f) * 1e6) / 1e4).replace(".", ","));
const pctFracao = (t: string): number | null => {
  const s = t.trim().replace("%", "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 && n < 100 ? Math.round(n * 1e4) / 1e6 : Number.NaN;
};

function DialogoPerfil({ perfil, aoFechar, aoGravar }: { perfil: Perfil | null; aoFechar: () => void; aoGravar: () => void }) {
  const notify = useNotify();
  const [nome, setNome] = useState(perfil?.nome ?? "");
  const [valores, setValores] = useState<Record<Campo, string>>({
    margem: pctTexto(perfil?.margem),
    seguro: pctTexto(perfil?.seguro),
    custo_fixo: pctTexto(perfil?.custo_fixo),
    simples: pctTexto(perfil?.simples),
  });
  const [observacao, setObservacao] = useState(perfil?.observacao ?? "");
  const [ativo, setAtivo] = useState(perfil?.ativo ?? true);
  const [gravando, setGravando] = useState(false);
  const fracoes = Object.fromEntries(CAMPOS.map((c) => [c.id, pctFracao(valores[c.id])])) as Record<Campo, number | null>;
  const invalido = Object.values(fracoes).some((v) => v === null || Number.isNaN(v));
  const soma = Object.values(fracoes).reduce<number>((s, v) => s + (v ?? 0), 0);

  async function gravar() {
    setGravando(true);
    const dados = { nome: nome.trim(), ...fracoes, observacao: observacao.trim() || null, ativo };
    const { error } = perfil ? await supabase.from("perfil_preco").update(dados).eq("id", perfil.id) : await supabase.from("perfil_preco").insert(dados);
    setGravando(false);
    if (error) {
      notify(/perfil_preco_nome_key/.test(error.message) ? "Já existe um perfil com esse nome." : `Não foi possível gravar: ${error.message}`, { type: "error" });
      return;
    }
    notify("Perfil gravado.", { type: "success" });
    aoGravar();
  }

  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="sm">
      <DialogTitle>{perfil ? `Perfil “${perfil.nome}”` : "Novo perfil de regra"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} fullWidth required autoFocus placeholder="Ex.: Competitivo" />
          <Grid container spacing={1.5}>
            {CAMPOS.map((c) => (
              <Grid key={c.id} size={{ xs: 6, sm: 3 }}>
                <TextField
                  size="small"
                  label={`${c.nome} (%)`}
                  value={valores[c.id]}
                  onChange={(e) => setValores((v) => ({ ...v, [c.id]: e.target.value }))}
                  error={Number.isNaN(fracoes[c.id])}
                  required
                  fullWidth
                  slotProps={{ htmlInput: { inputMode: "decimal" } }}
                />
              </Grid>
            ))}
          </Grid>
          {!invalido && soma >= 1 && <Alert severity="error">A soma dos percentuais precisa ser menor que 100%.</Alert>}
          <TextField label="Quando usar (opcional)" value={observacao} onChange={(e) => setObservacao(e.target.value)} fullWidth multiline />
          {perfil && <FormControlLabel control={<Switch checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />} label="Ativo" />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void gravar()} disabled={gravando || !nome.trim() || invalido || soma >= 1}>
          {gravando ? "Gravando…" : "Gravar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PerfisPreco() {
  const { permissions } = usePermissions<Permissoes | null>();
  const admin = permissions?.papel === "admin";
  const { data: perfis = [], refetch } = useGetList<Perfil>("perfil_preco", { pagination: { page: 1, perPage: 100 }, sort: { field: "nome", order: "ASC" } });
  const [editando, setEditando] = useState<Perfil | "novo" | null>(null);
  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: "1rem" }}>
            Perfis de regra de preço
          </Typography>
          {admin && (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setEditando("novo")}>
              Novo perfil
            </Button>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Escolhido na precificação da licitação, o perfil preenche margem, seguro, custo fixo e Simples. Cada preço gravado guarda os valores usados.
        </Typography>
        {perfis.length === 0 ? (
          <Alert severity="info">Nenhum perfil cadastrado. Sem perfil, a licitação usa o padrão de preço vigente.</Alert>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Perfil</TableCell>
                  {CAMPOS.map((c) => (
                    <TableCell key={c.id} align="right">
                      {c.nome}
                    </TableCell>
                  ))}
                  <TableCell>Quando usar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {perfis.map((p) => (
                  <TableRow key={p.id} hover={admin} onClick={admin ? () => setEditando(p) : undefined} sx={{ cursor: admin ? "pointer" : "default", opacity: p.ativo ? 1 : 0.5 }}>
                    <TableCell>{p.nome}</TableCell>
                    {CAMPOS.map((c) => (
                      <TableCell key={c.id} align="right">
                        {formatarPercentual(p[c.id])}
                      </TableCell>
                    ))}
                    <TableCell>{p.observacao ?? ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      {editando && (
        <DialogoPerfil
          perfil={editando === "novo" ? null : editando}
          aoFechar={() => setEditando(null)}
          aoGravar={() => {
            setEditando(null);
            void refetch();
          }}
        />
      )}
    </Card>
  );
}
